// A mock draft in the browser (MOCK_DRAFT.md §3 and §5, BACKLOG D16).
//
// THE SAME ROOM AS THE LIVE DRAFT, HOSTED HERE. The server sends a kit once
// (`GET /api/leagues/:id/draft/mock`); this builds the pure room from it
// (`@fsp/draft-room`) with the person in the seat they chose and a bot in every
// other, and does what the live worker does for a real room — wakes it when
// something is due and hands it each intent — on a clock of its own. Nothing
// here decides a pick or a rule: that is the room's and the engine's.
//
// A CLOCK OF ITS OWN. The room's time is the device's plus an offset that
// jumps ahead to each bot's pick, so Instant is instant and Quick waits about
// a second in real time; on my own turns it runs in real time, which is what
// "the league's clock" means. No wall clock is read here (lint): the screen
// hands one in, as it does to the live room's hook.
//
// NOTHING IS WRITTEN WHILE IT RUNS. The room's state lives in this hook and
// goes when the page does; a mock that finishes hands back the record that
// keeps it (`record`, MOCK_DRAFT.md §6.1), which the screen sends. The setup
// panel says both before Start.
//
// THE SEATING, THE GRADES AND THE RECORD ARE THE ROOM PACKAGE'S
// (`@fsp/draft-room`'s `mock.ts`), so another client runs a mock the same way.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DraftClientMessage, DraftKit, DraftView, SaveMockRequest } from '@fsp/domain';
import { currentTeam, spotsLeft, type DraftRecap } from '@fsp/draft';
import {
  Room,
  lobbyState,
  mockPrepared,
  mockRecap,
  savedMockOf,
  type MockSetup,
  type Viewer,
} from '@fsp/draft-room';
import { intentId, type RoomRefusal } from './liveDraft';

export type { MockSetup };

/** How long a Quick bot takes, in real time: long enough to watch the board fill. */
export const QUICK_PICK_MS = 900;

export interface MockDraft {
  view: DraftView;
  /** The room's clock now, in milliseconds. */
  now: number;
  myTeamId: string;
  send: (message: DraftClientMessage) => void;
  /** Take back my last pick, and every bot pick after it. */
  takeBack: () => void;
  canTakeBack: boolean;
  /** The grades, once every pick is in. */
  recap: DraftRecap | null;
  /** What keeps it, once every pick is in (`POST …/draft/mocks`). */
  record: SaveMockRequest | null;
}

export function useMockDraft(
  kit: DraftKit,
  setup: MockSetup,
  me: { userId: string; name: string },
  deviceNow: () => number,
  onRefused: (refusal: RoomRefusal) => void,
): MockDraft {
  const { userId, name } = me;
  const offset = useRef(0);
  const clock = useRef(deviceNow);
  clock.current = deviceNow;
  const vnow = useCallback(() => clock.current() + offset.current, []);
  const refused = useRef(onRefused);
  refused.current = onRefused;

  const built = useMemo(() => {
    offset.current = 0;
    const { prepared, myTeamId } = mockPrepared(kit, setup, userId, name);
    const room = new Room(prepared, lobbyState(prepared), 0);
    // PRESENT, so a clock that runs out on me picks that turn and leaves my
    // seat as it was, as the live room does for somebody in it.
    const connected: ReadonlySet<string> = new Set([myTeamId]);
    room.start(clock.current(), 'You', connected);
    const viewer: Viewer = { userId, name, teamId: myTeamId, isCommissioner: true };
    return { room, viewer, connected, myTeamId };
  }, [kit, setup, userId, name]);

  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // THE HOST'S ONE JOB: wake the room when something is due.
  useEffect(() => {
    const { room, connected, myTeamId } = built;
    if (room.state.status !== 'live') return;
    const at = room.nextAlarm(connected);
    if (at === null) return;
    // MY TURN, or in an auction a player up I can still bid on, or my
    // nomination: the room runs in real time then, so I can answer. A bot's
    // nomination, and everything once I am on auto or full, jumps ahead.
    const myTurn = (): boolean => {
      if (room.state.autopilot[myTeamId]) return false;
      const a = room.state.auction;
      if (a === undefined) return currentTeam(room.state) === myTeamId;
      if (spotsLeft(room.state, myTeamId) <= 0 && a.lots.every((l) => l.leader !== myTeamId))
        return false;
      return a.lots.length > 0 || a.nominator === myTeamId;
    };
    const jumpTo = (due: number): void => {
      const now = vnow();
      if (due > now) offset.current += due - now;
      room.tick(vnow(), connected);
    };
    if (myTurn()) {
      // MY OWN CLOCK RUNS IN REAL TIME.
      const t = setTimeout(
        () => {
          room.tick(vnow(), connected);
          bump();
        },
        Math.max(0, at - vnow()),
      );
      return () => clearTimeout(t);
    }
    if (setup.bots === 'instant') {
      const t = setTimeout(() => {
        // Every pick up to my next turn, at once; to the end on auto.
        for (let i = 0; i < 2000 && room.state.status === 'live' && !myTurn(); i += 1) {
          const due = room.nextAlarm(connected);
          if (due === null) break;
          jumpTo(due);
        }
        bump();
      }, 0);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      jumpTo(at);
      bump();
    }, QUICK_PICK_MS);
    return () => clearTimeout(t);
  }, [built, version, setup.bots, vnow, bump]);

  const send = useCallback(
    (message: DraftClientMessage) => {
      const step = built.room.intent(built.viewer, message, vnow(), built.connected);
      if (step.reply?.type === 'intent.rejected') {
        refused.current({
          intentId: step.reply.payload.intentId,
          code: step.reply.payload.code,
          message: step.reply.payload.message,
        });
      }
      bump();
    },
    [built, vnow, bump],
  );

  const takeBack = useCallback(() => {
    const { room, viewer, connected, myTeamId } = built;
    const mine = room.state.picks.map((p) => p.teamId).lastIndexOf(myTeamId);
    if (mine < 0) return;
    // ONE UNDO AT A TIME, every pick after mine and then mine, with nothing
    // woken between: my turn comes back with the board as it was.
    while (room.state.picks.length > mine) {
      const step = room.intent(
        viewer,
        { type: 'draft.undo', payload: { intentId: intentId() } },
        vnow(),
        connected,
      );
      if (step.reply?.type === 'intent.rejected') break;
    }
    bump();
  }, [built, vnow, bump]);

  const { room, viewer, connected, myTeamId } = built;
  // `version` is the room's change: the room is mutable, and the version is
  // what says it moved.
  const view = useMemo(
    () => room.view(viewer, connected, vnow()),
    [room, viewer, connected, version, vnow],
  );
  const recap = useMemo(
    () => (room.state.status === 'complete' ? mockRecap(kit, room, myTeamId, name) : null),
    [kit, room, myTeamId, name, version],
  );
  const record = useMemo(
    () => savedMockOf(room, setup, myTeamId, recap),
    [room, setup, myTeamId, recap],
  );
  return {
    view,
    now: vnow(),
    myTeamId,
    send,
    takeBack,
    canTakeBack:
      room.state.status !== 'complete' && room.state.picks.some((p) => p.teamId === myTeamId),
    recap,
    record,
  };
}
